import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab,
    Badge,
    Stack,
    SelectChangeEvent
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
    CalendarToday,
    Person,
    School,
    CheckCircle,
    Cancel,
    FilterList,
    Search,
    BarChart,
    PieChart,
    Timeline,
    Download,
    Refresh,
    ThumbUp,
    ThumbDown,
    DateRange,
    AccessTime
} from '@mui/icons-material';
import { format, parseISO, isToday, isThisWeek, isThisMonth } from 'date-fns';
import { tr } from 'date-fns/locale';
import { alpha } from '@mui/material/styles';
import { BirebirRaporError } from '../../../types/ErrorModals/BirebirRaporError';
import { getBirebirRapor } from '../../../services/api/birebirRaporService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    }
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(2),
    '& .MuiCardHeader-title': {
        fontSize: '1.1rem',
        fontWeight: 600,
    },
    '& .MuiCardHeader-subheader': {
        color: alpha(theme.palette.common.white, 0.8),
    }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1.5),
    '&.MuiTableCell-head': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main,
        fontWeight: 600,
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: alpha(theme.palette.action.hover, 0.5),
    },
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    transition: 'background-color 0.2s',
}));

const FilterContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    }
}));

const StatCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'translateY(-4px)',
    }
}));

interface BirebirRaporItem {
    recNo: number;
    ogrenciId: number;
    ogrenciAdi: string;
    ogretmenId: number;
    ogretmenAdi: string;
    ders: string;
    konu: string;
    dersTarihi: string;
    saat: string;
    mekan: string;
    sonuc: number; // 1: Yapıldı, 2: Yapılmadı
    isStudentApproved: boolean;
    isTeacherApproved: boolean;
}

interface BirebirRaporRequestParams {
    kurumKodu: string;
    baslangicTarihi: string;
    bitisTarihi: string;
    sezon: number;
}

const BirebirRaporlari: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // State
    const [selectedSeason, setSelectedSeason] = useState<string>('2025');
    const [selectedTab, setSelectedTab] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: format(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd')
    });
    const [filters, setFilters] = useState({
        ogretmen: '',
        ders: '',
        onayDurumu: ''
    });

    const availableSeasons = ['2023', '2024', '2025', '2026', '2027'];

    // Handle tab change
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);

        // Adjust date range based on selected tab
        const today = new Date();
        let startDate = '';

        switch (newValue) {
            case 0: // Bugün
                startDate = format(today, 'yyyy-MM-dd');
                break;
            case 1: // Bu Hafta
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - today.getDay());
                startDate = format(weekStart, 'yyyy-MM-dd');
                break;
            case 2: // Bu Ay
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                startDate = format(monthStart, 'yyyy-MM-dd');
                break;
            case 3: // Özel Aralık
                // Keep existing date range
                return;
        }

        setDateRange({
            startDate,
            endDate: format(today, 'yyyy-MM-dd')
        });
    };

    // Handle season change
    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };

    // Handle date change
    const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle search
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    // Handle filter change
    const handleFilterChange = (field: keyof typeof filters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Query
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['birebirRapor', selectedSeason, dateRange],
        queryFn: async () => {
            try {
                const params: BirebirRaporRequestParams = {
                    kurumKodu: localStorage.getItem('kurumKodu') || '',
                    baslangicTarihi: dateRange.startDate,
                    bitisTarihi: dateRange.endDate,
                    sezon: parseInt(selectedSeason) + 1
                };

                return await getBirebirRapor(params);
            } catch (err) {
                const birebirError = err as BirebirRaporError;
                throw new Error(birebirError.message);
            }
        },
        enabled: !!localStorage.getItem('kurumKodu'),
        retry: 1
    });

    // Filter data
    const filteredData = React.useMemo(() => {
        if (!data) return [];

        return data.filter(item => {
            // Search filter
            const searchLower = searchText.toLowerCase();
            const matchesSearch =
                !searchText ||
                (item.ogrenciAdi && item.ogrenciAdi.toLowerCase().includes(searchLower)) ||
                (item.ogretmenAdi && item.ogretmenAdi.toLowerCase().includes(searchLower)) ||
                (item.ders && item.ders.toLowerCase().includes(searchLower)) ||
                (item.konu && item.konu.toLowerCase().includes(searchLower));

            // Other filters
            const matchesOgretmen = !filters.ogretmen || item.ogretmenAdi === filters.ogretmen;
            const matchesDers = !filters.ders || item.ders === filters.ders;

            let matchesOnay = true;
            if (filters.onayDurumu === 'onaylı') {
                matchesOnay = item.isStudentApproved && item.isTeacherApproved;
            } else if (filters.onayDurumu === 'öğrenci') {
                matchesOnay = item.isStudentApproved && !item.isTeacherApproved;
            } else if (filters.onayDurumu === 'öğretmen') {
                matchesOnay = !item.isStudentApproved && item.isTeacherApproved;
            } else if (filters.onayDurumu === 'onaysız') {
                matchesOnay = !item.isStudentApproved && !item.isTeacherApproved;
            }

            return matchesSearch && matchesOgretmen && matchesDers && matchesOnay;
        });
    }, [data, searchText, filters]);

    // Calculate statistics
    const statistics = React.useMemo(() => {
        if (!data) return { total: 0, completed: 0, approved: { both: 0, student: 0, teacher: 0, none: 0 } };

        const total = data.length;
        const completed = data.filter(item => item.sonuc === 1).length;

        const bothApproved = data.filter(item => item.isStudentApproved && item.isTeacherApproved).length;
        const onlyStudentApproved = data.filter(item => item.isStudentApproved && !item.isTeacherApproved).length;
        const onlyTeacherApproved = data.filter(item => !item.isStudentApproved && item.isTeacherApproved).length;
        const noneApproved = data.filter(item => !item.isStudentApproved && !item.isTeacherApproved).length;

        return {
            total,
            completed,
            approved: {
                both: bothApproved,
                student: onlyStudentApproved,
                teacher: onlyTeacherApproved,
                none: noneApproved
            }
        };
    }, [data]);

    // Get unique options for filters
    const filterOptions = React.useMemo(() => {
        if (!data) return { ogretmenler: [], dersler: [] };

        const ogretmenler = Array.from(new Set(data.map(item => item.ogretmenAdi)));
        const dersler = Array.from(new Set(data.map(item => item.ders)));

        return {
            ogretmenler: ogretmenler.sort(),
            dersler: dersler.sort()
        };
    }, [data]);

    // Export to Excel function
    const exportToExcel = () => {
        // Implementation would go here
        alert('Excel export functionality would be implemented here');
    };

    // Render statistics cards
    const renderStatisticsCards = () => {
        return (
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                <CalendarToday />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">{statistics.total}</Typography>
                                <Typography variant="body2" color="text.secondary">Toplam Birebir Ders</Typography>
                            </Box>
                        </CardContent>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                                <CheckCircle />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">{statistics.completed}</Typography>
                                <Typography variant="body2" color="text.secondary">Tamamlanan Dersler</Typography>
                            </Box>
                        </CardContent>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                                <ThumbUp />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">{statistics.approved.both}</Typography>
                                <Typography variant="body2" color="text.secondary">Tam Onaylı Dersler</Typography>
                            </Box>
                        </CardContent>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <StatCard>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                                <ThumbDown />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" fontWeight="bold">{statistics.approved.none}</Typography>
                                <Typography variant="body2" color="text.secondary">Onaysız Dersler</Typography>
                            </Box>
                        </CardContent>
                    </StatCard>
                </Grid>
            </Grid>
        );
    };

    // Render approval distribution card
    const renderApprovalDistribution = () => {
        return (
            <StyledCard sx={{ mb: 4 }}>
                <StyledCardHeader
                    title="Onay Durumu Dağılımı"
                    avatar={<PieChart />}
                />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
                                        <Typography variant="body2">Tam Onaylı</Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="bold">{statistics.approved.both}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: theme.palette.info.main }} />
                                        <Typography variant="body2">Sadece Öğrenci Onaylı</Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="bold">{statistics.approved.student}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: theme.palette.secondary.main }} />
                                        <Typography variant="body2">Sadece Öğretmen Onaylı</Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="bold">{statistics.approved.teacher}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: theme.palette.error.main }} />
                                        <Typography variant="body2">Onaysız</Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="bold">{statistics.approved.none}</Typography>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* Burada bir grafik kütüphanesi kullanılabilir (Chart.js, Recharts vb.) */}
                            <Box sx={{
                                width: 200,
                                height: 200,
                                borderRadius: '50%',
                                position: 'relative',
                                background: `conic-gradient(
                  ${theme.palette.success.main} 0% ${statistics.approved.both / statistics.total * 100}%, 
                  ${theme.palette.info.main} ${statistics.approved.both / statistics.total * 100}% ${(statistics.approved.both + statistics.approved.student) / statistics.total * 100}%, 
                  ${theme.palette.secondary.main} ${(statistics.approved.both + statistics.approved.student) / statistics.total * 100}% ${(statistics.approved.both + statistics.approved.student + statistics.approved.teacher) / statistics.total * 100}%, 
                  ${theme.palette.error.main} ${(statistics.approved.both + statistics.approved.student + statistics.approved.teacher) / statistics.total * 100}% 100%
                )`
                            }}>
                                <Box sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 120,
                                    height: 120,
                                    borderRadius: '50%',
                                    bgcolor: 'background.paper',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                }}>
                                    <Typography variant="h5" fontWeight="bold">{statistics.total}</Typography>
                                    <Typography variant="caption" color="text.secondary">Toplam Ders</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </StyledCard>
        );
    };

    return (
        <Box>
            {/* Header with title and export button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
            }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Birebir Dersler Raporu
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={exportToExcel}
                    >
                        Excel'e Aktar
                    </Button>

                    <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                        <Select
                            labelId="season-select-label"
                            id="season-select"
                            value={selectedSeason}
                            onChange={handleSeasonChange}
                            label="Eğitim Sezonu"
                        >
                            {availableSeasons.map((season) => (
                                <MenuItem key={season} value={season}>
                                    {season} - {(parseInt(season) + 1)} Eğitim Yılı
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Tooltip title="Yenile">
                        <IconButton onClick={() => refetch()} color="primary">
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Date range tabs */}
            <Paper sx={{
                mb: 3,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: alpha(theme.palette.primary.main, 0.05)
            }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: '48px',
                            fontWeight: 500
                        },
                        '& .Mui-selected': {
                            color: theme.palette.primary.main,
                            fontWeight: 600
                        }
                    }}
                >
                    <Tab icon={<CalendarToday />} label={isMobile ? undefined : "Bugün"} iconPosition="start" />
                    <Tab icon={<DateRange />} label={isMobile ? undefined : "Bu Hafta"} iconPosition="start" />
                    <Tab icon={<Timeline />} label={isMobile ? undefined : "Bu Ay"} iconPosition="start" />
                    <Tab icon={<AccessTime />} label={isMobile ? undefined : "Özel Aralık"} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Custom date range inputs (only show when "Özel Aralık" is selected) */}
            {selectedTab === 3 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Başlangıç Tarihi"
                            type="date"
                            fullWidth
                            value={dateRange.startDate}
                            onChange={(e) => handleDateChange('startDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Bitiş Tarihi"
                            type="date"
                            fullWidth
                            value={dateRange.endDate}
                            onChange={(e) => handleDateChange('endDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Statistics cards */}
            {!isLoading && !error && data && renderStatisticsCards()}

            {/* Approval distribution */}
            {!isLoading && !error && data && data.length > 0 && renderApprovalDistribution()}

            {/* Search and filters */}
            <FilterContainer>
                <TextField
                    placeholder="Ara: Öğrenci, Öğretmen, Ders..."
                    variant="outlined"
                    size="small"
                    fullWidth={isMobile}
                    value={searchText}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                    }}
                    sx={{ flexGrow: 1, maxWidth: { sm: 300 } }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="teacher-filter-label">Öğretmen</InputLabel>
                    <Select
                        labelId="teacher-filter-label"
                        value={filters.ogretmen}
                        onChange={(e) => handleFilterChange('ogretmen', e.target.value)}
                        label="Öğretmen"
                    >
                        <MenuItem value="">Tümü</MenuItem>
                        {filterOptions.ogretmenler.map(ogretmen => (
                            <MenuItem key={ogretmen} value={ogretmen}>{ogretmen}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="lesson-filter-label">Ders</InputLabel>
                    <Select
                        labelId="lesson-filter-label"
                        value={filters.ders}
                        onChange={(e) => handleFilterChange('ders', e.target.value)}
                        label="Ders"
                    >
                        <MenuItem value="">Tümü</MenuItem>
                        {filterOptions.dersler.map(ders => (
                            <MenuItem key={ders} value={ders}>{ders}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="approval-filter-label">Onay Durumu</InputLabel>
                    <Select
                        labelId="approval-filter-label"
                        value={filters.onayDurumu}
                        onChange={(e) => handleFilterChange('onayDurumu', e.target.value)}
                        label="Onay Durumu"
                    >
                        <MenuItem value="">Tümü</MenuItem>
                        <MenuItem value="onaylı">Tam Onaylı</MenuItem>
                        <MenuItem value="öğrenci">Sadece Öğrenci Onaylı</MenuItem>
                        <MenuItem value="öğretmen">Sadece Öğretmen Onaylı</MenuItem>
                        <MenuItem value="onaysız">Onaysız</MenuItem>
                    </Select>
                </FormControl>
            </FilterContainer>

            {/* Data display */}
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    sx={{ mt: 2, borderRadius: 2 }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block">
                                CorrelationId: {(error as BirebirRaporError).correlationId}
                            </Typography>
                        )
                    }
                >
                    {(error as Error).message}
                </Alert>
            ) : !data?.length ? (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    Seçilen tarih aralığında birebir ders kaydı bulunmamaktadır.
                </Alert>
            ) : filteredData.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    Arama kriterlerine uygun birebir ders kaydı bulunmamaktadır.
                </Alert>
            ) : (
                <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Tarih</StyledTableCell>
                                <StyledTableCell>Saat</StyledTableCell>
                                <StyledTableCell>Öğrenci</StyledTableCell>
                                <StyledTableCell>Öğretmen</StyledTableCell>
                                <StyledTableCell>Ders</StyledTableCell>
                                <StyledTableCell>Konu</StyledTableCell>
                                <StyledTableCell>Durum</StyledTableCell>
                                <StyledTableCell>Onay</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((item, index) => (
                                <StyledTableRow key={index}>
                                    <StyledTableCell>
                                        {format(parseISO(item.dersTarihi), 'dd MMMM yyyy', { locale: tr })}
                                    </StyledTableCell>
                                    <StyledTableCell>{item.saat}</StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                                {item.ogrenciAdi ? item.ogrenciAdi.charAt(0) : '?'}
                                            </Avatar>
                                            <Typography variant="body2">{item.ogrenciAdi}</Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: theme.palette.secondary.main }}>
                                                {item.ogretmenAdi ? item.ogretmenAdi.charAt(0) : '?'}
                                            </Avatar>
                                            <Typography variant="body2">{item.ogretmenAdi}</Typography>
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            label={item.ders}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                borderColor: theme.palette.primary.main,
                                                color: theme.palette.primary.main
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                maxWidth: 150,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {item.konu || '-'}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Chip
                                            icon={item.sonuc === 1 ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                                            label={item.sonuc === 1 ? 'Yapıldı' : 'Yapılmadı'}
                                            size="small"
                                            color={item.sonuc === 1 ? 'success' : 'error'}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="Öğrenci Onayı">
                                                <Chip
                                                    icon={<Person fontSize="small" />}
                                                    label="Öğrenci"
                                                    size="small"
                                                    color={item.isStudentApproved ? 'success' : 'default'}
                                                    variant={item.isStudentApproved ? 'filled' : 'outlined'}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Öğretmen Onayı">
                                                <Chip
                                                    icon={<School fontSize="small" />}
                                                    label="Öğretmen"
                                                    size="small"
                                                    color={item.isTeacherApproved ? 'success' : 'default'}
                                                    variant={item.isTeacherApproved ? 'filled' : 'outlined'}
                                                />
                                            </Tooltip>
                                        </Stack>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default BirebirRaporlari;